import {
	Box,
	Button,
	Alert,
	IconButton,
	Menu,
	MenuItem,
	Divider,
	CircularProgress,
	Typography,
} from '@mui/material';
import { EasTooltip } from 'ui/EasTooltip';
import Joyride, { ACTIONS, CallBackProps, STATUS, Step } from 'react-joyride';
import {
	ArrowDropDown,
	Block,
	Cancel,
	ContactMail,
	ControlPointDuplicate,
	Error as ErrorIcon,
	History,
	MoreVert,
	OpenInNew,
	PersonAdd,
	PersonRemove,
	Settings,
	Tour,
} from '@mui/icons-material';
import { HelpButton } from 'ui/HelpButton';
import { PATHS, ROLES } from 'routes/constants';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useState, useMemo, useRef, useEffect } from 'react';
import Masonry from 'masonry-layout';
import {
	activateEmerchantRequest,
	activateEuDdpRequest,
	blockEmerchantRequest,
	duplicateEmerchantRequest,
	getEmEnumsRequest,
	getEmPaymentMethodsRequest,
	registerNominatedPersonAccountRequest,
	resetActivateEuDdp,
	unblockEmerchantRequest,
} from 'redux/reducers/emManagement/reducer';
import { useDispatch, useSelector } from 'react-redux';
import {
	getActivateEuDdpLoading,
	getEmByIdFull,
	getEmByIdFullLoading,
	getEmPaymentMethodsData,
	getUpdateEmLoading,
	getUpdateEmerchantWarning,
	getDuplicateEmerchantLoading,
	getEmByIdFullError,
} from 'redux/reducers/emManagement/selectors';
import { getCountryListData } from 'redux/reducers/country/selectors';
import { notificationContainer } from 'utils/notificationContainer';
import { TEmManagementByIdFullResponse } from 'redux/reducers/emManagement/types';
import { TBoxesStructureItem, emBoxesStructure } from './emBoxesStructure';
import { EmSettingsDialog } from './EmSettingsDialog';
import './styles.css';
import { getUserInfo } from '../../../redux/reducers/user/selectors';
import { ConfirmationDialog } from '../../../ui/ConfirmationDialog';
import { BlockConfirmationDialog } from '../../../ui/BlockConfirmationDialog';
import {
	attachEmUserRequest,
	detachEmUserRequest,
} from '../../../redux/reducers/accessManagement/reducer';
import {
	getEmKeycloakUsers,
	getEmKeycloakUsersFailure,
} from '../../../redux/reducers/accessManagement/selectors';
import { EMManagementIdHeader } from './EMManagementIdHeader';
import { saveUrl, saveUrl2 } from '../../../redux/reducers/auth/reducer';
import { EHelpButtonArticles } from '../../../ui/HelpButton/constants';
import { getEmAttachments } from '../../../redux/reducers/attachments/selectors';
import { EmSites } from './EmSites';
import { EEmEnums } from '../../../redux/reducers/emManagement/constants';
import { EMManagementIdBody } from './EMManagementIdBody';
import { getSavedUrl, getSavedUrl2 } from '../../../redux/reducers/auth/selectors';
import { emManagementActions } from './actions';
import { COLORS } from '../../../services/constants/colors';
import { ActivateEUDdpDialog } from './ActivateEUDdpDialog';
import { TActivateEUDdpFormValues } from './ActivateEUDdpDialog/types';
import { searchEuDdpRecordsRequest } from '../../../redux/reducers/euDdp/reducer';
import {
	selectEuDdpRecordsSearch,
	selectEuDdpRecordsSearchLoading,
} from '../../../redux/reducers/euDdp/selectors';
import { ESortDirection } from '../../../ui/EasTable/types';
import { getStoresByClientIdRequest } from '../../../redux/reducers/shopifyStores/reducer';
import { SendSmsDialog } from './SendSmsDialog';
import { GenerateUploadLinkDialog } from './GenerateUploadLinkDialog';
import { EEmStatus } from '../constants';

export const EMManagementId = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { emId } = useParams<{ emId?: string }>();
	const user = useSelector(getUserInfo);
	const emDataFull = useSelector(getEmByIdFull);
	const emDataFullLoading = useSelector(getEmByIdFullLoading);
	const emDataFullError = useSelector(getEmByIdFullError);
	const updateEmLoading = useSelector(getUpdateEmLoading);
	const duplicateEmerchantLoading = useSelector(getDuplicateEmerchantLoading);
	const keycloakUsers = useSelector(getEmKeycloakUsers);
	const emKeycloakUsersFailure = useSelector(getEmKeycloakUsersFailure);
	const updateEmWarning = useSelector(getUpdateEmerchantWarning);
	const emAttachments = useSelector(getEmAttachments);
	const countryList = useSelector(getCountryListData);
	const activateEuDdpLoading = useSelector(getActivateEuDdpLoading);
	const euDdpRecords = useSelector(selectEuDdpRecordsSearch);
	const euDdpRecordsLoading = useSelector(selectEuDdpRecordsSearchLoading);
	const euDdpRecord = useMemo(() => {
		const candidate = euDdpRecords?.data?.[0];
		if (!candidate || emId == null) return null;
		return candidate.emId === Number(emId) ? candidate : null;
	}, [euDdpRecords, emId]);
	const normalizedFullName = emDataFull?.fullName ? emDataFull.fullName.trim() : '';
	const blockTargetName = normalizedFullName.length > 0 ? normalizedFullName : 'Selected EM';
	const blockDialogDescription = `${blockTargetName} will be blocked. Access to dashboard and to EAS solution functionality will be revoked. Are you sure you want to block client access?`;
	const savedUrl = useSelector(getSavedUrl);
	const savedUrl2 = useSelector(getSavedUrl2);
	const isCurrentUserConnected = !!keycloakUsers?.data?.some(
		(item) => item.email?.toLowerCase?.() === user?.email?.toLowerCase?.(),
	);

	const dispatch = useDispatch();
	const [isSettingsOpen, setIsSettingsOpen] = useState(false);
	const [showHideHint, setShowHideHint] = useState(false);
	const [settingsTooltipOpen, setSettingsTooltipOpen] = useState(false);
	const hideHintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [boxesStructure, setBoxesStructure] = useState<TBoxesStructureItem[]>(
		emBoxesStructure(
			emDataFull ?? null,
			emDataFullLoading,
			updateEmLoading,
			euDdpRecord,
			euDdpRecordsLoading,
		),
	);
	const [openBlockDialog, setOpenBlockDialog] = useState(false);
	const setCloseBlockDialog = () => {
		setOpenBlockDialog(false);
	};
	const [openConnectDialog, setOpenConnectDialog] = useState(false);
	const [openDisconnectDialog, setOpenDisconnectDialog] = useState(false);
	const [openRegisterNominatedPersonAccountDialog, setOpenRegisterNominatedPersonAccountDialog] =
		useState(false);
	const [openUnblockDialog, setOpenUnblockDialog] = useState(false);
	const [openDuplicateUserDialog, setOpenDuplicateUserDialog] = useState(false);
	const [openSendSmsDialog, setOpenSendSmsDialog] = useState(false);
	const [openGenerateUploadLinkDialog, setOpenGenerateUploadLinkDialog] = useState(false);
	const [moreActionsAnchor, setMoreActionsAnchor] = useState<null | HTMLElement>(null);
	const gridRef = useRef<HTMLDivElement>(null);
	const msnryRef = useRef<Masonry | null>(null);
	const resizeObserverRef = useRef<ResizeObserver | null>(null);
	// const registeredCountry = useGetCountryById(
	// 	emDataFull?.registeredCountry && emDataFull.registeredCountry > 0
	// 		? emDataFull.registeredCountry
	// 		: 0,
	// );
	// Merge fresh base structure (with latest data/loading flags) with saved user settings (show/width)
	const mergedStructure = useMemo(() => {
		const base = emBoxesStructure(
			emDataFull ?? null,
			emDataFullLoading,
			updateEmLoading,
			euDdpRecord,
			euDdpRecordsLoading,
		);
		const savedById = new Map(boxesStructure.map((b) => [b.id, b]));
		return base
			.filter(
				(box) =>
					!box.roles || !!user?.roles?.some((role) => box.roles?.includes(role.roleName as ROLES)),
			)
			.map((box) => {
				const saved = savedById.get(box.id);
				return saved ? { ...box, show: saved.show, width: saved.width } : box;
			});
	}, [
		user,
		emDataFull,
		emDataFullLoading,
		updateEmLoading,
		euDdpRecord,
		euDdpRecordsLoading,
		boxesStructure,
	]);

	const allVisibleItems = useMemo(
		() => mergedStructure.filter((item) => item.show),
		[mergedStructure],
	);

	const [tourRun, setTourRun] = useState(false);

	const tourSteps = useMemo<Step[]>(
		() =>
			[
				{ target: '#em-name', content: 'Legal entity full name' },
				{ target: '#em-id', content: 'Internal EAS ID of the entity' },
				{ target: '#em-registered-country', content: 'Registered country' },
				{ target: '#em-created-date', content: 'Date when entity was created' },
				{ target: '#em-active-services', content: 'Enabled EAS services' },
				{ target: '#em-legal-status', content: 'Legal status of the entity' },
				{ target: '#hmrc-status', content: 'HMRC connection status (UK tax authority)' },
				{ target: '#em-payment-method', content: 'Payment method for the entity' },
				{ target: '#em-status', content: 'Current entity status' },
				{
					target: '#em-activate-user-button',
					content: 'Activate nominated person user account when required',
				},
				{ target: '#em-block-button', content: 'Block EM access to dashboard and EAS' },
				{ target: '#em-unblock-button', content: 'Unblock EM and restore access' },
				{ target: '#em-connect-button', content: 'Connect your account to this EM' },
				{ target: '#em-disconnect-button', content: 'Disconnect your account from this EM' },
				{ target: '#em-change-history-button', content: 'View change history' },
				{ target: '#em-communication-history-button', content: 'View communication history' },
				{ target: '#em-display-settings-button', content: 'Customize visible info blocks' },
				{ target: '#em-help-button', content: 'Help and documentation for action buttons' },
				{ target: '#em-start-tour-button', content: 'Start this guided tour' },
				{ target: '#em-widget-em-management', content: 'Main information and EM details' },
				{ target: '#em-widget-landed-cost-settings', content: 'Landed cost settings' },
				{ target: '#em-widget-fee-configuration', content: 'Fee configuration' },
				{
					target: '#em-widget-em-special-registrations',
					content: 'EM special registrations (e.g. IOSS, HMRC)',
				},
				{ target: '#em-widget-access-management', content: 'Access management and EM users' },
				{ target: '#em-widget-em-attachments', content: 'EM attachments' },
				{ target: '#em-widget-em-additional-services', content: 'EM additional services' },
				{ target: '#em-widget-vat-numbers-management', content: 'VAT numbers management' },
				{ target: '#em-widget-em-comments', content: 'EM comments' },
				{
					target: '#em-widget-em-notifications-management',
					content: 'EM notifications management',
				},
				{ target: '#em-widget-last-invoices', content: 'Last invoices' },
				{ target: '#em-widget-em-automation-scripts', content: 'EM automation scripts' },
			].map((step) => ({ ...step, disableBeacon: true })),
		[],
	);

	const handleTourCallback = (data: CallBackProps) => {
		const { action, status } = data;
		if (status === STATUS.FINISHED || status === STATUS.SKIPPED || action === ACTIONS.CLOSE) {
			setTourRun(false);
		}
	};

	useEffect(() => {
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.FEES_CLASS }));
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.ANNUAL_FEE_TYPE_ID }));
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.INVOICE_FEE_TYPE_ID }));
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.TIMEZONE_LIST }));
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.WEIGHT_UNIT }));
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.CUSTOMS_DATA_STATUS }));
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.PAYMENT_MODE }));
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.IOSS_SALE_OVER_THRESHOLD_OPTION }));
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.SUPPORTED_CURRENCY }));
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.EM_REPORTING_FEE_ACTUAL }));
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.SITE_INTEGRATION_TYPE }));
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.SITE_PLATFORM }));
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.LEGAL_STATUS_EU }));
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.MANUAL_FEE_CLASS }));
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.MONTHLY_FEES }));
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.SERVICE_TYPE }));
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.IOSS_DUTY_MODES }));
		dispatch(
			searchEuDdpRecordsRequest({
				pageNumber: 1,
				pageSize: 1,
				searchString: '',
				filters: [
					{
						fieldName: 'emId',
						// name: 'emId',
						value: String(emId),
						operator: '=',
						// comparator: '=',
					},
				],
				// orderBy: [{ fieldName: 'id', direction: ESortDirection.DESC }],
			}),
		);
	}, [dispatch, emId]);

	useEffect(() => {
		if (!gridRef.current) {
			return;
		}

		msnryRef.current = new Masonry(gridRef.current, {
			itemSelector: '.grid-item',
			columnWidth: '.grid-sizer',
			percentPosition: true,
		});

		const layout = () => {
			msnryRef.current?.layout?.();
		};

		window.addEventListener('resize', layout);

		const ro = new ResizeObserver(() => {
			layout();
		});
		resizeObserverRef.current = ro;
		const items = gridRef.current.querySelectorAll('.grid-item');
		items.forEach((el) => ro.observe(el));

		requestAnimationFrame(() => {
			msnryRef.current?.reloadItems?.();
			layout();
		});

		return () => {
			window.removeEventListener('resize', layout);
			resizeObserverRef.current?.disconnect();
			resizeObserverRef.current = null;
			msnryRef.current?.destroy?.();
			msnryRef.current = null;
		};
	}, []);

	// Re-observe items and relayout when visible items set changes
	useEffect(() => {
		if (!gridRef.current || !resizeObserverRef.current) {
			return;
		}
		const ro = resizeObserverRef.current;
		ro.disconnect();
		const items = gridRef.current.querySelectorAll('.grid-item');
		items.forEach((el) => ro.observe(el));

		requestAnimationFrame(() => {
			msnryRef.current?.reloadItems?.();
			msnryRef.current?.layout?.();
		});
	}, [allVisibleItems.length]);

	// Relayout when data payloads or structure update
	useEffect(() => {
		requestAnimationFrame(() => {
			msnryRef.current?.layout?.();
		});
	}, [emDataFull, boxesStructure]);

	// const handleReplayHistory = () => {
	// 	if (savedUrl) {
	// 		if (savedUrl.includes(PATHS.EM_MANAGEMENT_PAGE)) navigate(savedUrl);
	// 	} else navigate(PATHS.EM_MANAGEMENT_PAGE);
	// };

	const handleSettingsOpen = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
		setIsSettingsOpen(true);
	};

	const handleSettingsClose = () => {
		setAnchorEl(null);
		setIsSettingsOpen(false);
	};

	const handleSettingsSave = (newStructure: TBoxesStructureItem[]) => {
		setBoxesStructure(newStructure);
		try {
			const minimalToSave = newStructure.map((item) => ({
				id: item.id,
				label: item.label,
				show: item.show,
				width: item.width,
			}));
			localStorage.setItem(`emBoxesStructure_${location.pathname}`, JSON.stringify(minimalToSave));
		} catch {
			// ignore storage errors
		}
	};

	const handleHideBox = (id: string) => {
		const newStructure = mergedStructure.map((item) =>
			item.id === id ? { ...item, show: false } : item,
		);
		handleSettingsSave(newStructure);
		setShowHideHint(true);
		if (hideHintTimerRef.current) {
			clearTimeout(hideHintTimerRef.current);
		}
		hideHintTimerRef.current = setTimeout(() => setShowHideHint(false), 6000);
	};

	useEffect(
		() => () => {
			if (hideHintTimerRef.current) {
				clearTimeout(hideHintTimerRef.current);
			}
		},
		[],
	);

	// Load saved settings from localStorage and merge with current base structure
	useEffect(() => {
		const key = `emBoxesStructure_${location.pathname}`;
		const saved = localStorage.getItem(key);
		if (!saved) return;
		try {
			const parsed = JSON.parse(saved) as Array<{
				id: string;
				label?: string;
				show: boolean;
				width: number;
			}>;
			const base = emBoxesStructure(
				emDataFull ?? null,
				emDataFullLoading,
				updateEmLoading,
				euDdpRecord,
				euDdpRecordsLoading,
			);
			const merged = base.map((box) => {
				const found = parsed.find((p) => p && p.id === box.id);
				return found ? { ...box, show: !!found.show, width: found.width ?? box.width } : box;
			});
			// Only update if structure actually differs
			if (JSON.stringify(merged) !== JSON.stringify(boxesStructure)) {
				setBoxesStructure(merged);
			}
		} catch {
			// ignore malformed saved structure
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, emDataFull, emDataFullLoading, updateEmLoading]);

	const handleDisconnectFromEm = () => {
		dispatch(
			detachEmUserRequest({
				body: { email: user?.email ?? '', emId: Number(emId) },
				getEmKeycloakUsers: { emId: Number(emId), pageNumber: 1, pageSize: 100 },
			}),
		);
		setOpenDisconnectDialog(false);
	};

	const handleConnectToEm = () => {
		const email = user?.email;
		if (!email) return;
		dispatch(
			attachEmUserRequest({
				body: { email, emId: Number(emId) },
				getEmKeycloakUsers: { emId: Number(emId), pageNumber: 1, pageSize: 100 },
			}),
		);

		setOpenConnectDialog(false);
	};

	const handleRegisterNominatedPersonAccount = () => {
		if (
			!emDataFull ||
			!emDataFull.fullName ||
			!emDataFull.nominatedEmail ||
			!emDataFull.nominatedPerson ||
			!emDataFull.identifier
		)
			return;
		// dispatch(
		// 	registerNominatedPersonAccountRequest({
		// 		id: Number(emId),
		// 		full_name: emDataFull.fullName,
		// 		nominated_email: emDataFull.nominatedEmail,
		// 		nominated_person: emDataFull.nominatedPerson,
		// 		nominated_email_secondary: null,
		// 		nominated_person_secondary: null,
		// 		identifier: emDataFull.identifier,
		// 	}),
		// );
		dispatch(activateEmerchantRequest({ id: Number(emId) }));
		setOpenRegisterNominatedPersonAccountDialog(false);
	};

	const handleBlockEM = () => {
		dispatch(blockEmerchantRequest({ id: Number(emId) }));
	};

	const handleUnblockEM = () => {
		dispatch(unblockEmerchantRequest({ id: Number(emId) }));
		setOpenUnblockDialog(false);
	};

	const handleDuplicateUser = () => {
		dispatch(duplicateEmerchantRequest({ id: Number(emId) }));
		setOpenDuplicateUserDialog(false);
	};

	const [openActivateEUDdpDialog, setOpenActivateEUDdpDialog] = useState(false);
	const handleCloseActivateEUDdpDialog = () => {
		dispatch(resetActivateEuDdp());
		setOpenActivateEUDdpDialog(false);
	};

	// const handleActivateEUDdp = (values: TActivateEUDdpFormValues) => {
	// 	if (values.country == null) {
	// 		notificationContainer('Please select a country', 'error');
	// 		return;
	// 	}
	// 	const countryItem = countryList?.find((c) => c.id === values.country);
	// 	const countryCode = countryItem?.isoAlpha2;
	// 	if (!countryCode) {
	// 		notificationContainer('Please select a valid country', 'error');
	// 		return;
	// 	}
	// 	dispatch(
	// 		activateEuDdpRequest({
	// 			emId: Number(emId),
	// 			data: {
	// 				streetNo: values.streetNo,
	// 				region: values.region,
	// 				city: values.city,
	// 				postalCode: values.postalCode,
	// 				country: countryCode,
	// 				pricingPlanId: values.pricingPlanId,
	// 				personalFixedFee: values.personalFixedFee,
	// 				personalPercentageFee: values.personalPercentageFee,
	// 			},
	// 		}),
	// 	);
	// };

	const handleMoreActions = (name: string) => {
		if (name === 'Activate EU DDP') {
			dispatch(resetActivateEuDdp());
			setOpenActivateEUDdpDialog(true);
		}
		if (name === 'Send SMS') {
			setOpenSendSmsDialog(true);
		}
		if (name === 'Generate link for uploading file') {
			setOpenGenerateUploadLinkDialog(true);
		}
	};

	useEffect(() => {
		if (emDataFull?.clientId) {
			dispatch(getStoresByClientIdRequest({ client_id: String(emDataFull?.clientId) }));
		}
	}, [dispatch, emDataFull?.clientId]);

	if (emDataFullError)
		return (
			<Box
				sx={{
					padding: '60px 10px 0 10px',
					color: COLORS.PRIMARY,
					textAlign: 'center',
					fontWeight: 'bold',
				}}
			>
				<ErrorIcon sx={{ fontSize: '6rem', p: 0, m: 0 }} />
				<Typography variant="h3" sx={{ fontWeight: 'bold', mt: 4 }}>
					Something went wrong.
				</Typography>
				<Typography variant="h2" sx={{ fontWeight: 'bold', mt: 4 }}>
					We couldn&apos;t load the EM data.
				</Typography>
			</Box>
		);

	return (
		<Box sx={{ padding: '0px 10px 0 10px', maxWidth: '100vw', overflowX: 'auto', height: '100%' }}>
			<Joyride
				steps={tourSteps}
				run={tourRun}
				continuous
				showSkipButton
				showProgress
				spotlightPadding={8}
				styles={{
					options: { zIndex: 2000 },
					tooltip: {
						borderRadius: '12px',
						fontSize: '1rem',
					},
					buttonNext: {
						backgroundColor: '#016193',
						color: '#fff',
						borderRadius: '12px',
						fontSize: '0.9rem',
					},
					buttonSkip: {
						fontSize: '0.9rem',
						color: '#016193',
						fontWeight: 'bold',
					},
					buttonBack: {
						color: '#016193',
						fontSize: '0.9rem',
						fontWeight: 'bold',
					},
				}}
				callback={handleTourCallback}
			/>
			<Box sx={{ minWidth: '1400px', width: '100%', height: '100%' }}>
				<Box
					sx={{
						m: '0 10px 10px 10px',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<EMManagementIdHeader savedUrl={savedUrl} />
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, m: 0, p: 0 }}>
						{(emDataFullLoading ||
							updateEmLoading ||
							euDdpRecordsLoading ||
							duplicateEmerchantLoading) && <CircularProgress size={20} />}

						{emKeycloakUsersFailure && (
							<Button
								id="em-activate-user-button"
								variant="contained"
								color="primary"
								onClick={() => setOpenRegisterNominatedPersonAccountDialog(true)}
								size="small"
								disabled={emDataFullLoading}
							>
								Activate User Account
							</Button>
						)}
						{(emDataFull?.status === EEmStatus.ACTIVE ||
							emDataFull?.status === EEmStatus.INACTIVE) && (
							<EasTooltip title="Block EM">
								<IconButton
									id="em-block-button"
									color="error"
									disabled={emDataFullLoading}
									onClick={() => setOpenBlockDialog(true)}
									sx={{ p: 0, m: 0 }}
								>
									<Block sx={{ fontSize: '2rem', p: 0, m: 0 }} />
								</IconButton>
							</EasTooltip>
						)}

						{emDataFull?.status === EEmStatus.BLOCKED && (
							<EasTooltip title="Unblock EM">
								<IconButton
									id="em-unblock-button"
									color="warning"
									onClick={() => setOpenUnblockDialog(true)}
									disabled={emDataFullLoading}
									sx={{ p: 0, m: 0 }}
								>
									<Cancel sx={{ fontSize: '2rem', p: 0, m: 0 }} />
								</IconButton>
							</EasTooltip>
						)}
						<EasTooltip title="Duplicate User Account">
							<IconButton
								id="em-duplicate-user-button"
								color="primary"
								onClick={() => setOpenDuplicateUserDialog(true)}
								sx={{ p: 0, m: 0 }}
								disabled={emDataFullLoading}
							>
								<ControlPointDuplicate sx={{ fontSize: '2rem', p: 0, m: 0 }} />
							</IconButton>
						</EasTooltip>

						{isCurrentUserConnected ? (
							<EasTooltip title="Disconnect your account from EM">
								<IconButton
									id="em-disconnect-button"
									color="primary"
									onClick={() => setOpenDisconnectDialog(true)}
									disabled={emDataFullLoading}
									sx={{ p: 0, m: 0 }}
								>
									<PersonRemove sx={{ fontSize: '2rem', p: 0, m: 0 }} />
								</IconButton>
							</EasTooltip>
						) : (
							<EasTooltip title="Connect your account to EM">
								<IconButton
									id="em-connect-button"
									color="primary"
									onClick={() => setOpenConnectDialog(true)}
									disabled={emKeycloakUsersFailure || emDataFullLoading}
									sx={{ p: 0, m: 0 }}
								>
									<PersonAdd sx={{ fontSize: '2rem', p: 0, m: 0 }} />
								</IconButton>
							</EasTooltip>
						)}
						<EasTooltip title="Change History">
							<IconButton
								id="em-change-history-button"
								color="primary"
								onClick={() => {
									dispatch(saveUrl2(window.location.pathname + window.location.search));

									navigate(
										`${PATHS.EM_MANAGEMENT_CHANGE_HISTORY_PAGE.replace(':emId', emId ?? '')}`,
									);
								}}
								disabled={emDataFullLoading}
								sx={{ p: 0, m: 0 }}
							>
								<History sx={{ fontSize: '2rem', p: 0, m: 0 }} />
							</IconButton>
						</EasTooltip>

						<EasTooltip title="Communication History">
							<IconButton
								id="em-communication-history-button"
								color="primary"
								onClick={() => {
									dispatch(saveUrl2(window.location.pathname + window.location.search));

									navigate(
										`${PATHS.EM_MANAGEMENT_COMMUNICATION_HISTORY_PAGE.replace(':emId', emId ?? '')}`,
									);
								}}
								disabled={emDataFullLoading}
							>
								<ContactMail sx={{ fontSize: '2rem', p: 0, m: 0 }} />
							</IconButton>
						</EasTooltip>

						<>
							{/* <Button
								id="em-more-actions-button"
								variant="outlined"
								color="primary"
								size="small"
								disabled={emDataFullLoading}
								endIcon={<ArrowDropDown />}
								onClick={(e) => setMoreActionsAnchor(e.currentTarget)}
								sx={{ fontWeight: 'bold', color: COLORS.PRIMARY }}
							>
								More Actions
							</Button> */}
							<EasTooltip title="More Actions">
								<IconButton
									id="em-more-actions-button"
									color="primary"
									onClick={(e) => setMoreActionsAnchor(e.currentTarget)}
									disabled={emDataFullLoading}
									sx={{ p: 0, m: 0 }}
								>
									<MoreVert sx={{ fontSize: '2rem', p: 0, m: 0 }} />
								</IconButton>
							</EasTooltip>
							<Menu
								anchorEl={moreActionsAnchor}
								open={Boolean(moreActionsAnchor)}
								onClose={() => setMoreActionsAnchor(null)}
								anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
								transformOrigin={{ vertical: 'top', horizontal: 'right' }}
							>
								{emManagementActions(
									Number(emId),
									emDataFull?.fullName ?? '',
									!emDataFull?.activeServices?.includes('EU DDP'),
								).map((action) => (
									<MenuItem
										key={action.name}
										onClick={() => {
											setMoreActionsAnchor(null);
											if (action.path) {
												window.open(action.path, '_blank', 'noopener,noreferrer');
											} else {
												handleMoreActions(action.name);
											}
										}}
										color="primary"
										sx={{ fontWeight: 'bold', color: COLORS.PRIMARY }}
									>
										{action.path && <OpenInNew sx={{ fontSize: '0.8rem', p: 0, m: 0, mr: 0.5 }} />}
										{action.name}
									</MenuItem>
								))}
							</Menu>
						</>
						{/* <Button
							id="em-start-tour-button"
							variant="contained"
							color="primary"
							onClick={() => setTourRun(true)}
							size="small"
							disabled={emDataFullLoading}
							startIcon={<Tour />}
						>
							Start Tour
						</Button> */}
						<Divider
							orientation="vertical"
							flexItem
							sx={{ my: 1, height: '2rem', borderColor: COLORS.SECONDARY, borderWidth: '1.2px' }}
						/>
						<EasTooltip title="Start Tour">
							<IconButton
								id="em-start-tour-button"
								color="primary"
								onClick={() => setTourRun(true)}
								size="small"
								disabled={emDataFullLoading}
								sx={{ p: 0, m: 0 }}
							>
								<Tour sx={{ fontSize: '1.5rem', p: 0, m: 0 }} />
							</IconButton>
						</EasTooltip>
						<Box id="em-help-button" component="span">
							<HelpButton
								type={EHelpButtonArticles.EM_PAGE_ACTION_BUTTONS}
								sx={{ fontSize: '1.5rem' }}
							/>
						</Box>
						<EasTooltip
							title={
								showHideHint
									? 'You can manage block visibility in Display Settings'
									: 'Display Settings'
							}
							type={showHideHint ? 'warning' : 'default'}
							open={settingsTooltipOpen || showHideHint}
							onOpen={() => setSettingsTooltipOpen(true)}
							onClose={() => setSettingsTooltipOpen(false)}
							arrow
						>
							<IconButton
								id="em-display-settings-button"
								onClick={handleSettingsOpen}
								color="primary"
								disabled={emDataFullLoading}
								sx={{ p: 0, m: 0 }}
							>
								<Settings sx={{ fontSize: '1.5rem', p: 0, m: 0 }} />
							</IconButton>
						</EasTooltip>
					</Box>
				</Box>
				<EmSites
					emDataFull={emDataFull ?? ({} as TEmManagementByIdFullResponse)}
					load={emDataFullLoading}
				/>

				{updateEmWarning && (
					<Alert
						variant="filled"
						severity="warning"
						sx={{ m: 0.5 }}
						// onClose={() => {}}
					>
						{updateEmWarning}
					</Alert>
				)}

				<EMManagementIdBody
					boxesStructure={mergedStructure}
					// onHideBox={handleHideBox}
				/>

				{/* <div ref={gridRef} id="em-grid" className="grid" style={{ height: '100%' }}>
					<div className="grid-sizer" style={{ height: '100%' }} />
					{allVisibleItems.map(item => (
						<MasonryItem
							key={item.id}
							item={item}
							data={emDataFull ?? ({} as TEmManagementByIdFullResponse)}
							banner={updateEmWarning || undefined}
						/>
					))}
				</div> */}
				{/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
					<EmSpecialRegistrations />
					<EmAdditionalServices />
				</Box> */}
			</Box>
			<EmSettingsDialog
				open={isSettingsOpen}
				onClose={handleSettingsClose}
				anchorEl={anchorEl}
				boxesStructure={mergedStructure}
				onSave={handleSettingsSave}
			/>
			<ConfirmationDialog
				open={openConnectDialog}
				setClose={() => setOpenConnectDialog(false)}
				handleConfirm={handleConnectToEm}
				title="Connect to EM"
				text="Are you sure you want to connect your account to EM?"
			/>
			<ConfirmationDialog
				open={openDisconnectDialog}
				setClose={() => setOpenDisconnectDialog(false)}
				handleConfirm={handleDisconnectFromEm}
				title="Disconnect from EM"
				text="Are you sure you want to disconnect your account from EM?"
			/>
			<ConfirmationDialog
				open={openRegisterNominatedPersonAccountDialog}
				setClose={() => setOpenRegisterNominatedPersonAccountDialog(false)}
				handleConfirm={handleRegisterNominatedPersonAccount}
				title="Activate User Account"
				// text="Are you sure you want to activate user account?"
				disableConfirm={
					!emDataFull ||
					!emDataFull.fullName ||
					!emDataFull.nominatedEmail ||
					!emDataFull.nominatedPerson ||
					!emDataFull.identifier
				}
			>
				{!emDataFull ||
				!emDataFull.fullName ||
				!emDataFull.nominatedEmail ||
				!emDataFull.nominatedPerson ||
				!emDataFull.identifier ? (
					<Alert variant="filled" severity="warning">
						Activation of the user account cannot be completed due to missing required information.
						Please ensure that the necessary details:{' '}
						{!emDataFull?.nominatedEmail && <b>Nominated Email</b>}
						{!emDataFull?.nominatedEmail && !emDataFull?.nominatedPerson && ', '}
						{!emDataFull?.nominatedPerson && <b>Nominated Person</b>}, are provided in the EM Main
						Information section
					</Alert>
				) : (
					'Are you sure you want to activate user account?'
				)}
			</ConfirmationDialog>
			<BlockConfirmationDialog
				open={openBlockDialog}
				setClose={setCloseBlockDialog}
				// description={blockDialogDescription}
				emId={Number(emId)}
				blockTargetName={blockTargetName}
			/>
			<ConfirmationDialog
				open={openUnblockDialog}
				setClose={() => setOpenUnblockDialog(false)}
				handleConfirm={handleUnblockEM}
				title="Unblock EM"
				text="Are you sure you want to unblock EM?"
			/>
			<ActivateEUDdpDialog
				open={openActivateEUDdpDialog}
				onClose={handleCloseActivateEUDdpDialog}
			/>
			<SendSmsDialog
				open={openSendSmsDialog}
				onClose={() => setOpenSendSmsDialog(false)}
				emId={Number(emId)}
				phone={emDataFull?.phone}
			/>
			<GenerateUploadLinkDialog
				open={openGenerateUploadLinkDialog}
				onClose={() => setOpenGenerateUploadLinkDialog(false)}
				emId={Number(emId)}
			/>
			<ConfirmationDialog
				open={openDuplicateUserDialog}
				setClose={() => setOpenDuplicateUserDialog(false)}
				handleConfirm={handleDuplicateUser}
				title="Duplicate User Account"
			>
				<Typography variant="body1">Are you sure you want to duplicate this account?</Typography>

				<Typography variant="body1" sx={{ mt: 1 }}>
					Duplicating this account will automatically apply changes to the original account:
					<br />
					- an &apos;x&apos; will be added to the Full Name; <br />- a space will be added to the
					TIN.
				</Typography>
				<Typography variant="body1" sx={{ mt: 1 }}>
					This action cannot be undone. All settings will be copied to new account from original.
				</Typography>
			</ConfirmationDialog>
		</Box>
	);
};
